import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Send, X, Loader2 } from 'lucide-react';
import {
  listConversations,
  createConversation,
  getConversation,
  listMessages,
  streamMessage,
  type SseEvent,
} from '../api/chatApi';
import type { ChatMessage, Conversation } from '../types/contracts';

// Плавающий мини-чат с ассистентом, доступный на любой странице (вмонтирован в DesktopShell).
//
// Важно: это Не отдельный/урезанный ассистент, а тот же самый оркестратор,
// что и страница «Assistant» (та же /api/conversations/.../messages/stream). Говорит
// с одной постоянной беседой (тайтл совпадает с NAV_CONV_TITLE), которая видна
// в списке диалогов на странице ассистента — ничего не скрыто и не двоится.
// Навигация по сайту работает только тут (clientCapabilities: ['navigate']) —
// сервер растознает явные команды типа «открой связи» и шлёт событие navigate.

const NAV_CONV_TITLE = 'Быстрый чат';
const LS_CONV_KEY = 'oa-floating-nav-conv-id';
const LS_OPEN_KEY = 'oa-floating-nav-open';

export function FloatingNavigator() {
  const routerNavigate = useNavigate();
  const [open, setOpen] = useState<boolean>(() => {
    try { return localStorage.getItem(LS_OPEN_KEY) === '1'; } catch { return false; }
  });
  const [convId, setConvId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const initedRef = useRef(false);

  useEffect(() => {
    try { localStorage.setItem(LS_OPEN_KEY, open ? '1' : '0'); } catch { /* ignore */ }
  }, [open]);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages, streaming]);

  const ensureConversation = useCallback(async (): Promise<Conversation | null> => {
    // 1) Пробуем уже известный id из localStorage (быстрый путь, без листинга).
    let savedId: string | null = null;
    try { savedId = localStorage.getItem(LS_CONV_KEY); } catch { /* ignore */ }
    if (savedId) {
      try {
        const conv = await getConversation(savedId);
        return conv;
      } catch { /* беседа удалена/архивна — создадим/найдём заново ниже */ }
    }
    // 2) Ищем среди существующих бесед по заголовку (на случай другого браузера/устройства).
    try {
      const list = await listConversations(false);
      const found = list.find((c) => (c.title || '') === NAV_CONV_TITLE);
      if (found) {
        try { localStorage.setItem(LS_CONV_KEY, found.id); } catch { /* ignore */ }
        return found;
      }
    } catch { /* ignore */ }
    // 3) Создаём новую.
    try {
      const conv = await createConversation(NAV_CONV_TITLE);
      try { localStorage.setItem(LS_CONV_KEY, conv.id); } catch { /* ignore */ }
      return conv;
    } catch {
      return null;
    }
  }, []);

  const init = useCallback(async () => {
    if (initedRef.current) return;
    initedRef.current = true;
    setLoading(true);
    const conv = await ensureConversation();
    if (conv) {
      setConvId(conv.id);
      try { setMessages(await listMessages(conv.id)); } catch { /* ignore */ }
    }
    setLoading(false);
  }, [ensureConversation]);

  useEffect(() => {
    if (open) void init();
  }, [open, init]);

  const onToggle = useCallback(() => {
    setOpen((v) => !v);
  }, []);

  const onClose = useCallback(() => {
    abortRef.current?.abort();
    setStreaming(false);
    setOpen(false);
  }, []);

  const send = useCallback(async () => {
    const trimmed = draft.trim();
    if (!trimmed || streaming) return;
    let id = convId;
    if (!id) {
      const conv = await ensureConversation();
      if (!conv) return;
      id = conv.id;
      setConvId(id);
    }
    setDraft('');
    setStreaming(true);
    const ac = new AbortController();
    abortRef.current = ac;
    const tempUser: ChatMessage = {
      id: 'tmp-' + Date.now(),
      conversationId: id,
      role: 'user',
      content: trimmed,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempUser]);
    let acc = '';
    await streamMessage({
      conversationId: id,
      text: trimmed,
      clientCapabilities: ['navigate'],
      signal: ac.signal,
      onEvent: (ev: SseEvent) => {
        if (ev.type === 'user_message') {
          setMessages((prev) => prev.map((m) => (m.id === tempUser.id ? ev.message : m)));
        } else if (ev.type === 'delta') {
          acc += ev.text;
          setMessages((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last && last.role === 'assistant' && last.id.startsWith('stream-')) {
              next[next.length - 1] = { ...last, content: acc };
            } else {
              next.push({ id: 'stream-' + Date.now(), conversationId: id!, role: 'assistant', content: acc, createdAt: new Date().toISOString() });
            }
            return next;
          });
        } else if (ev.type === 'navigate') {
          routerNavigate(ev.path);
        } else if (ev.type === 'done' || ev.type === 'error') {
          setStreaming(false);
        }
      },
      onError: () => setStreaming(false),
    });
    setStreaming(false);
    abortRef.current = null;
  }, [draft, streaming, convId, ensureConversation, routerNavigate]);

  return (
    <div className="oa-floatnav">
      {open ? (
        <div className="oa-floatnav-panel" role="dialog" aria-label="Быстрый чат с ассистентом">
          <div className="oa-floatnav-head">
            <span><MessageCircle size={16} /> Быстрый чат</span>
            <button className="oa-floatnav-x" onClick={onClose} aria-label="Закрыть"><X size={16} /></button>
          </div>
          <div ref={scrollerRef} className="oa-floatnav-body">
            {loading ? (
              <div className="oa-floatnav-hint"><Loader2 className="spin" size={14} /> Загрузка…</div>
            ) : messages.length === 0 ? (
              <div className="oa-floatnav-hint">Спросите что угодно или напишите «открой связи», «покажи маркетплейс» — и я перейду туда.</div>
            ) : (
              messages.map((m, i) => (
                <div key={m.id || i} className={`oa-floatnav-msg ${m.role}`}>
                  <div className="oa-floatnav-bubble">{m.content}</div>
                </div>
              ))
            )}
            {streaming && (!messages.length || messages[messages.length - 1]?.role !== 'assistant') ? (
              <div className="oa-floatnav-msg assistant"><div className="oa-floatnav-bubble oa-floatnav-fade">…</div></div>
            ) : null}
          </div>
          <div className="oa-floatnav-bar">
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); } }}
              placeholder="Сообщение или «открой…»"
              aria-label="Сообщение ассистенту"
            />
            <button onClick={() => void send()} disabled={streaming || !draft.trim()} aria-label="Отправить">
              {streaming ? <Loader2 className="spin" size={16} /> : <Send size={16} />}
            </button>
          </div>
        </div>
      ) : null}
      <button className="oa-floatnav-fab" onClick={onToggle} aria-label={open ? 'Свернуть чат' : 'Открыть быстрый чат'}>
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>
    </div>
  );
}
